import { useMemo, useState } from 'react';
import { TagChip } from 'features/result-content/TagChip';
import { isSelectableTag, localizedCopy, uniqueIds } from 'features/result-content/resultContent';

export function TagPicker({
  id,
  label,
  tags = [],
  value = [],
  onChange,
  locale,
  t,
  error,
  multiple = true,
  required,
  disabled,
}) {
  const [search, setSearch] = useState('');
  const selectedIds = uniqueIds(value);
  const selected = useMemo(
    () => selectedIds.map((tagId) => tags.find((tag) => Number(tag.id) === tagId)).filter(Boolean),
    [selectedIds, tags],
  );
  const selectable = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tags.filter((tag) => {
      if (!isSelectableTag(tag)) {
        return false;
      }
      if (selectedIds.includes(Number(tag.id))) {
        return false;
      }
      if (!term) {
        return true;
      }
      return localizedCopy(tag, locale, 'name').toLowerCase().includes(term) || String(tag.slug || '').toLowerCase().includes(term);
    });
  }, [locale, search, selectedIds, tags]);

  const add = (tagId) => {
    const numeric = Number(tagId);
    if (!numeric || selectedIds.includes(numeric)) {
      return;
    }
    onChange(multiple ? [...selectedIds, numeric] : [numeric]);
    setSearch('');
  };

  const remove = (tagId) => {
    onChange(selectedIds.filter((idValue) => idValue !== Number(tagId)));
  };

  return (
    <div className="field">
      <span className="field-label" id={`${id}-label`}>
        {label}
        {required ? ' *' : ''}
      </span>
      <div className="chip-row" aria-labelledby={`${id}-label`}>
        {selected.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            locale={locale}
            t={t}
            removable={!disabled && (multiple || selectedIds.length > 1 || isSelectableTag(tag))}
            onRemove={disabled ? undefined : remove}
          />
        ))}
      </div>
      <input
        id={id}
        className={error ? 'input input-error' : 'input'}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('resultContent.searchTags')}
        disabled={disabled || (!multiple && selectedIds.length > 0)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {search || selectable.length ? (
        <ul className="picker-list" aria-label={label}>
          {selectable.slice(0, 12).map((tag) => (
            <li key={tag.id}>
              <button type="button" className="picker-option" disabled={disabled} onClick={() => add(tag.id)}>
                <span
                  className="tag-chip-swatch"
                  aria-hidden="true"
                  style={{ background: tag.color || 'var(--brand)' }}
                />
                {localizedCopy(tag, locale, 'name')}
                {tag.slug ? <span className="muted"> · {tag.slug}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <span id={`${id}-error`} className="field-error">{Array.isArray(error) ? error[0] : error}</span> : null}
    </div>
  );
}

export function SingleTagSelect({
  id,
  label,
  tags = [],
  value,
  onChange,
  locale,
  t,
  error,
  required,
  disabled,
  currentTag,
}) {
  const options = tags.filter((tag) => isSelectableTag(tag) || Number(tag.id) === Number(value));
  const currentArchived = currentTag && !isSelectableTag(currentTag);

  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">
        {label}
        {required ? ' *' : ''}
      </span>
      {currentTag ? <TagChip tag={currentTag} locale={locale} t={t} /> : null}
      {currentArchived ? <p className="warning-banner">{t('resultContent.archivedTagReplace')}</p> : null}
      <select
        id={id}
        className={error ? 'input input-error' : 'input'}
        value={value ? String(value) : ''}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={Boolean(error)}
      >
        <option value="">{t('resultContent.selectTag')}</option>
        {options.map((tag) => (
          <option key={tag.id} value={String(tag.id)} disabled={!isSelectableTag(tag)}>
            {localizedCopy(tag, locale, 'name')}
            {tag.is_archived ? ` (${t('resultContent.archivedTag')})` : ''}
            {!tag.is_active ? ` (${t('filters.inactive')})` : ''}
          </option>
        ))}
      </select>
      {error ? <span className="field-error">{Array.isArray(error) ? error[0] : error}</span> : null}
    </label>
  );
}
